/**
 * frontend/src/App.jsx
 * =====================
 * Top-level router.
 *
 * Two distinct surfaces share this app:
 *
 *   /            Manager Dashboard (desktop-first, iframe in C7 admin)
 *   /partners    Partner CRM
 *   /checkins    Check-in history
 *   /settings    Winery settings, C7 credentials, billing
 *   /login       Manager login
 *
 *   /checkin     Staff Check-In PWA (mobile-first, saved to homescreen)
 *                Auth via ?token= param — no user login required.
 *
 * The two surfaces intentionally share the same React app but use
 * completely separate layouts and auth mechanisms.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { AuthProvider, useAuth } from './context/AuthContext'

// Manager dashboard pages
import Dashboard from './pages/Dashboard'
import Partners from './pages/Partners'
import CheckIns from './pages/CheckIns'
import Settings from './pages/Settings'
import Login from './pages/Login'
import Register from './pages/Register'

// Staff check-in PWA
import CheckIn from './pages/CheckIn'

// Layouts
import ManagerLayout from './components/ManagerLayout'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,       // 1 minute
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
})

// ─── Protected route wrapper ───────────────────────────────────────────────

function RequireAuth({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="text-gray-400 text-sm">Loading…</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return children
}

// ─── App ──────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* ── Staff Check-In PWA (no auth context needed) ── */}
            <Route path="/checkin" element={<CheckIn />} />

            {/* ── Manager login + registration (public) ── */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* ── Manager dashboard (protected, iframe in C7) ── */}
            <Route
              element={
                <RequireAuth>
                  <ManagerLayout />
                </RequireAuth>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/checkins" element={<CheckIns />} />
              <Route path="/settings" element={<Settings />} />
            </Route>

            {/* ── Fallback ── */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}
